<template>
  <div>
    <h1 class="report-header mb-3" align="center" style="font-weight:bold">
      Renewal Options Report
    </h1>

    <b-card no-body class="mb-3">
      <b-card-header><h2>Overview</h2></b-card-header>
      <!--Global warnings to be displayed in main overview-->

      <b-container fluid class="mt-4">
        <h3 class="overview-project-name">
          {{ currentProject.projectName }}
        </h3>
        <div class="overview-project-location">
          <strong>Location:</strong> {{ currentProject.city }}, {{ currentProject.state }}
        </div>
        <div v-if="currentProject.projectId != null && currentProject.projectId !== ''" class="overview-project-id">
          <strong>Project ID:</strong> {{ currentProject.projectId }}
        </div>
        <div class="overview-last-saved">
          <strong>Last Modified:</strong> {{ lastSavedDate }} at {{ lastSavedTime }}
        </div>

      </b-container>


      <b-container v-if="recommendedStrategies.length === 0" fluid>
        <hr>
        <p>Despite being a valid combination of inputs, RxPave cannot recommend any of the five treatments. Consider investigating the following inputs that may cause this to occur:</p>
        <ul>
          <li>
            <strong>PCR Scores:</strong> PCR scores above 80 would suggest a pavement’s condition is sufficiently high that rehabilitation may be unnecessary; RxPave will yield no viable recommendations. To accurately reflect current conditions, a segment’s PCR score should be younger than the last treatment, and the last treatment should be near or older than its typical design life.
          </li>
          <li>
            <strong>Existing Conditions:</strong> Ensure that existing conditions are still accurate. RxPave may disqualify treatments if their construction conflicts with certain surface or subgrade conditions.
          </li>
          <li>
            <strong>Distress Surveys:</strong> Overestimating the presence and severity of pavement distresses can cause RxPave to exclude all treatments. Assess whether a combination of perceived distresses may be confused with multiple individual distresses (or vice versa).
          </li>
        </ul>

      </b-container>

      <b-container v-if="recommendedStrategies.length > 0" fluid>
        <hr>
        <h6 style="font-style: italic">
          RxPave has determined the following rehabilitation strategies are viable:
        </h6>
        <ul>
          <li v-for="r in recommendedStrategies">
            {{ r.optionData.name }}
          </li>
        </ul>

        <div v-if="notRecommendedStrategies.length > 0" fluid class="mt-3">
          <h6 style="font-style: italic;">
            RxPave does not recommend the following rehabilitation strategies:
          </h6>
          <ul>
            <li v-for="r in notRecommendedStrategies">
              {{ r.optionData.name }}
            </li>
          </ul>
        </div>
      </b-container>


      <b-container fluid class="">
        <hr>
        <!--<b-alert variant="warning" :show="pcrWarningTriggered">-->
        <!--<strong>PCR Collection Date:</strong>> PCR reading is over 4 years old and may no longer accurately represent current conditions.-->
        <!--</b-alert>-->
        <!--<b-alert variant="warning" :show="pavementDesignWarningTriggered">-->
        <!--<strong>Differing Pavement Designs:</strong> You have indicated the presence of parking lanes, shoulders, or bicycle lanes. These pavement areas may experience different loading traffic conditions than the mainline and consequently may feature a different pavement design. Further investigation may be needed to determine whether or not these areas must also be rehabilited or preserved.-->
        <!--</b-alert>-->
        <!--End global warnings-->

        <h3>Renewal Option Scores</h3>
        <p>A viable treatment strategy means that your segment four conditions: (1) the PCR lies within a treatment’s recommended range; (2) the distress survey does not disqualify the treatment; (3) the distress survey score is or is within 1 point of the top score; and (4) there are no conflicts with existing surface or subsurface features. Failure to pass all criteria results in RxPave disqualifying a treatment.</p>
        <b-table :items="renewalScores"
                 :sort-by.sync="sortBy"
                 :sort-desc.sync="sortDesc"
                 :fields="fields"
                 responsive
                 style="text-align: center;"
        >
          <template slot="isViable" slot-scope="data">
            {{ data.item.isViable ? "Recommended" : "Not Recommended" }}
          </template>

          <template slot="name" slot-scope="data">
            {{ data.item.optionData.name }}
          </template>

          <template slot="pcrIsOk" slot-scope="data">
            {{ data.item.pcrIsOk ? "Pass" : "Fail" }}
          </template>

          <!--<template slot="questions" slot-scope="data">-->
          <!--</template>-->

          <template slot="recsWarnings" slot-scope="data">
            {{ recommendationCountsByOption[data.item.renewalOption] }}/{{ warningCountsByOption[data.item.renewalOption] }}
          </template>
        </b-table>
      </b-container>

      <b-container fluid v-if="pavementDesignWarningTriggered || pcrWarningTriggered">
        <hr>
        <h3>General Considerations</h3>
        <b-list-group v-if="pavementDesignWarningTriggered || pcrWarningTriggered" class="mb-3">
          <b-list-group-item v-if="pcrWarningTriggered" variant="warning">
            <strong>PCR Collection Date:</strong>> PCR reading is over 4 years old and may no longer accurately represent current conditions.
          </b-list-group-item>
          <b-list-group-item v-if="pavementDesignWarningTriggered" variant="warning">
            <strong>Differing Pavement Designs:</strong> You have indicated the presence of parking lanes, shoulders, or bicycle lanes. These pavement areas may experience different loading traffic conditions than the mainline and consequently may feature a different pavement design. Further investigation may be needed to determine whether or not these areas must also be rehabilited or preserved.
          </b-list-group-item>
        </b-list-group>
      </b-container>
    </b-card>

    <div v-for="option in renewalScores">
      <div class="pagebreak"></div>
      <RenewalOptionDetails :data="option"
                            :rankings="renewalRankings"
                            :warnings="createOptionWarnings(option.renewalOption)"
      />
    </div>

    <div class="pagebreak"></div>

    <UserInputsReport />
  </div>
</template>

<script>
  import { mapFields } from "vuex-map-fields";
  import { getAllRenewalOptionScores, createWarnings } from "../../../js/distress/renewalOptionPoints";

  import dayjs from 'dayjs';

  import RenewalOptionDetails from './reportComponents/RenewalOptionDetails'
  import UserInputsReport from './reportComponents/UserInputsReport'


  export default {
    name: "Renewal",
      components:{
          RenewalOptionDetails,
          UserInputsReport

      },
    computed: {
      ...mapFields("rx",[
        'currentProject'
      ]),
      renewalScores(){
        console.log("Running RenewalScores");
        // Update the results of the option scores with variant for display in table
        return getAllRenewalOptionScores(this.currentProject).map((r) => {
          r['_rowVariant'] = r['isViable'] ? 'success' : 'warning';
          return r
        });
      },
      renewalRankings(){
        const options = this.renewalScores.map((renewalOption) => {
          return {
            key: renewalOption.renewalOption,
            score: renewalOption.score,
            name: renewalOption.optionData.name,
          }
        });
        return options.sort((a,b) => b.score-a.score);
      },
      pcrWarningTriggered() {
        const currentYear = new Date().getFullYear();
        return (currentYear - this.currentProject.lastPCRYear) > 4
      },
      pavementDesignWarningTriggered(){
        return this.currentProject.hasParkingLanes || this.currentProject.hasShoulder || this.currentProject.hasBikeLane
      },
      recommendationCountsByOption(){
        const recommendationCounts = {};
        this.renewalScores.map((option) => {
          recommendationCounts[option.renewalOption] = option.warnings.length;
        });
        return recommendationCounts
      },
      warningCountsByOption(){
        const warningCounts = {};
        this.renewalScores.map((option) => {
          warningCounts[option.renewalOption] = 0;
          // Warning if distress is disqualifying
          warningCounts[option.renewalOption] += option.distressViable ? 0 : 1;

          // Warning if PCR out of bounds
          warningCounts[option.renewalOption] += option.pcrIsOk ? 0 : 1;
        });

        return warningCounts
      },
      lastSavedDate(){
        return dayjs(this.currentProject.lastModified.date)
          .subtract(new Date().getTimezoneOffset(), 'minute')
          .format('MMMM D, YYYY');
      },
      lastSavedTime(){
        return dayjs(this.currentProject.lastModified.date)
          .subtract(new Date().getTimezoneOffset(), 'minute')
          .format('hh:mm A');
      },
      notRecommendedStrategies(){
        return this.renewalScores.filter(r => !r.isViable );
      },
      recommendedStrategies(){
        return this.renewalScores.filter(r => r.isViable);
      }
    },
    data: function(){
      return {
        sortBy: 'score',
        sortDesc: true,
        fields: [
          {key: 'name', label:'Renewal Option'},
          {key: 'isViable', label: "Recommendation"},
          {key: 'pcrIsOk', label: "PCR Check"},
          {key: 'score', label: 'Total Score'},
          // {key: 'questions', label:'Questions Assessment'},
          {key: 'recsWarnings', label: 'Recommendations/Warnings'}
        ],
      }
    },
    methods:{
      createOptionWarnings(renewalOption){
        return createWarnings(renewalOption, this.currentProject, this.renewalScores)
      }
    }
  }
</script>

<style lang="scss">
  @import './../../../scss/custom.scss';

  h1 {
    color: $primary;
    margin-bottom: 0
  }
  .report-header {
    color: $primary;
  }

  hr {
    display: block;
    color: $gray-800;
    border-top: 1px solid;
    border-bottom: 1px solid;
  }

</style>

<style>
  .card-header > h1 {
    margin-bottom: 0
  }

  .card-header > h2 {
    margin-bottom: 0
  }

  @media print {

    body {
      margin: 0;
      width: 100%;
    }

    #router-container-outer-col{
      flex-basis: 100%;
      flex: 100%;
      max-width: 100%;
    }

    .pagebreak {
      page-break-before: always;
    }
    /*h1 { page-break-before: always }*/
    /*h2 { page-break-before: always }*/

    .card { display: block; width: 100% }
    .container { display: block; width: 100% }

  }

</style>